import type { HingeSide } from '../drawing/door';

/**
 * A made-to-measure cabinet: the model behind the cabinet builder.
 *
 * <p>Ported from the site mockup (`cabinets.html`) and extended with the
 * parity items of `DOOR_AND_CARCASS_BUILDERS_PLAN.md` B8:
 * <ol>
 *   <li>many cabinets in one order (see `orderState.ts`);</li>
 *   <li>the fronts each cabinet needs, SIZED FROM THE CARCASS, so doors and
 *       carcasses bought together always fit ({@link frontsFor});</li>
 *   <li>a measured drawing (`drawings.ts`);</li>
 *   <li>exact interior positions: shelf heights and drawer-front heights in mm,
 *       each resettable to standard ({@link standardShelves},
 *       {@link standardDrawerFronts}).</li>
 * </ol>
 *
 * <h3>Assumptions, until the carcass supplier's rules are known</h3>
 * The plan's open question "which supplier makes custom carcasses" is not yet
 * answered, so the front and plinth rules below are the common UK full-overlay
 * conventions (a 720 mm base carcass takes a 715 mm door; a 600 mm one a
 * 597 mm door; 3 mm between fronts; 150 mm legs). They live in {@link RULES} so a
 * supplier's own figures replace them in one place.
 */

export type CabinetType = 'base' | 'wall' | 'tall' | 'drawers' | 'corner' | 'wardrobe' | 'stairs';
export type CornerKind = 'l' | 'blind' | 'diag';
export type Build = 'rigid' | 'flat';

export const RULES = {
  /** Front height is the carcass height less this (top gap + bottom gap). */
  frontHeightLess: 5,
  /** Front width is the carcass width less this. */
  frontWidthLess: 3,
  /** Gap between two fronts. */
  frontGap: 3,
  /** Plinth or leg height under a floor-standing cabinet. */
  plinth: 150,
  /** Board thickness, for internal sizes. */
  board: 18,
  /** A drawer front lower than this cannot take a drawer box. */
  minDrawerFront: 110,
  /** Top drawer height in a stack of 3 or more. */
  topDrawer: 140,
  /** The widest a single door should be before two are needed. */
  maxSingleDoor: 600,
  /** Shelf span above which a shelf sags. */
  maxShelfSpan: 1000,
} as const;

/**
 * [wMin, wMax, hMin, hMax, dMin, dMax, hDefault, dDefault, wDefault] per type, limits from the
 * mockup. Base, wall, drawer and wardrobe defaults are a standard unit's sizes, so the builder
 * opens on a cabinet that is priced online (see standardMatch.ts); a change makes it made to measure.
 */
const LIMITS: Record<string, [number, number, number, number, number, number, number, number, number]> = {
  base: [150, 1200, 500, 900, 280, 600, 720, 570, 600],
  wall: [150, 1200, 250, 1200, 200, 400, 720, 300, 600],
  tall: [300, 700, 1200, 2400, 280, 650, 1970, 570, 600],
  drawers: [300, 1200, 500, 900, 400, 600, 720, 570, 600],
  wardrobe: [300, 1000, 1200, 2400, 400, 650, 2160, 600, 600],
  stairs: [600, 4000, 800, 2400, 400, 900, 2000, 600, 2700],
  'corner-l': [800, 1200, 500, 900, 500, 600, 720, 570, 900],
  'corner-blind': [800, 1300, 500, 900, 500, 600, 720, 570, 1000],
  'corner-diag': [550, 750, 500, 1000, 300, 350, 720, 300, 600],
};

export interface Limits {
  w: [number, number];
  h: [number, number];
  d: [number, number];
  defaults: { w: number; h: number; d: number };
}

export function limitsKey(type: CabinetType, corner: CornerKind): string {
  return type === 'corner' ? `corner-${corner}` : type;
}

export function limitsFor(type: CabinetType, corner: CornerKind = 'l'): Limits {
  const l = LIMITS[limitsKey(type, corner)] as (typeof LIMITS)[string];
  return { w: [l[0], l[1]], h: [l[2], l[3]], d: [l[4], l[5]], defaults: { w: l[8], h: l[6], d: l[7] } };
}

export type BayKind = 'pullout' | 'door' | 'double' | 'drawers' | 'open';

export const BAY_KIND_LABEL: Record<BayKind, string> = {
  pullout: 'Pull-out',
  door: 'Door',
  double: 'Double doors',
  drawers: 'Drawers',
  open: 'Open shelves',
};

export interface Bay {
  kind: BayKind;
  w: number;
  /** Drawers in a drawer bay. */
  n?: number;
}

export interface Cabinet {
  type: CabinetType;
  corner: CornerKind;
  /** Which side of a blind corner is blind. */
  blind: 'left' | 'right';
  w: number;
  /** Return width of an L-shaped corner, along the other wall. */
  b: number;
  h: number;
  /** Low-side height of an under-stairs run. */
  h2: number;
  d: number;
  qty: number;
  /** Doors on a base, wall, tall or wardrobe cabinet. */
  doors: number;
  /** Shelf heights in mm from the carcass bottom (inside). Empty is no shelves. */
  shelves: number[];
  shelvesCustom: boolean;
  /** Drawer-front heights, top to bottom, for a drawer unit. */
  drawerFronts: number[];
  drawersCustom: boolean;
  /** Hinge side of a single door. */
  hinge: 'left' | 'right';
  /**
   * A tall unit's upper door height: doors stacked, a lower door with this one above it, as the
   * standard broom and larder units are made. 0 is full-height doors.
   */
  upper: number;
  /** A blind corner's blank section, the part hidden under the return run. */
  blank: number;
  bays: Bay[];
  /** Include doors and drawer fronts; off buys the carcass only. */
  fronts: boolean;
  build: Build;
}

/**
 * The colour carcasses are drawn in. Not a choice: which board colours the
 * carcass supplier makes is still open (PALETTES_PLAN.md), so none is offered
 * and the desk confirms it with the quote.
 */
export const CARCASS_DRAWING_HEX = '#f1ede6';

export const TYPE_NAME: Record<string, string> = {
  base: 'Base unit',
  wall: 'Wall unit',
  tall: 'Tall unit',
  drawers: 'Drawer unit',
  wardrobe: 'Wardrobe carcass',
  stairs: 'Under-stairs unit',
  'corner-l': 'L-shaped corner base',
  'corner-blind': 'Blind corner base',
  'corner-diag': 'Diagonal wall corner',
};

export function typeName(c: Pick<Cabinet, 'type' | 'corner'>): string {
  return TYPE_NAME[limitsKey(c.type, c.corner)] ?? 'Cabinet';
}

export const DEFAULT_BAYS: Bay[] = [
  { kind: 'double', w: 600 },
  { kind: 'double', w: 600 },
  { kind: 'drawers', n: 3, w: 500 },
  { kind: 'drawers', n: 2, w: 500 },
  { kind: 'drawers', n: 1, w: 500 },
];

const whole = (v: number) => Math.round(v);

/** Inside height of the carcass (between top and bottom boards). */
export function insideHeight(c: Pick<Cabinet, 'h'>): number {
  return c.h - 2 * RULES.board;
}

/** Evenly spaced shelves inside the carcass. */
export function standardShelves(c: Pick<Cabinet, 'h'>, count: number): number[] {
  const inside = insideHeight(c);
  return Array.from({ length: count }, (_, i) => whole((inside / (count + 1)) * (i + 1)));
}

/** Standard drawer-front heights, top to bottom: a 140 mm top drawer in a stack of 3+, the rest equal. */
export function standardDrawerFronts(c: Pick<Cabinet, 'h'>, count: number): number[] {
  const n = Math.max(1, count);
  const total = c.h - RULES.frontHeightLess - RULES.frontGap * (n - 1);
  if (n < 3) return splitEvenly(total, n);
  const top = n >= 4 ? [RULES.topDrawer, RULES.topDrawer] : [RULES.topDrawer];
  return [...top, ...splitEvenly(total - top.reduce((s, v) => s + v, 0), n - top.length)];
}

function splitEvenly(total: number, n: number): number[] {
  const each = Math.floor(total / n);
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? total - each * (n - 1) : each));
}

export function defaultDoors(c: Pick<Cabinet, 'type' | 'w'>): number {
  if (c.type === 'drawers' || c.type === 'corner' || c.type === 'stairs') return 0;
  return c.w > RULES.maxSingleDoor ? 2 : 1;
}

export function newCabinet(type: CabinetType = 'base', corner: CornerKind = 'l'): Cabinet {
  const l = limitsFor(type, corner);
  const base = { type, corner, h: l.defaults.h, w: l.defaults.w };
  return {
    ...base,
    blind: 'left',
    b: 900,
    h2: 500,
    d: l.defaults.d,
    qty: 1,
    doors: defaultDoors(base),
    shelves: type === 'drawers' || type === 'stairs' ? [] : standardShelves(base, type === 'tall' ? 5 : type === 'wardrobe' ? 4 : 1),
    shelvesCustom: false,
    drawerFronts: standardDrawerFronts(base, 3),
    drawersCustom: false,
    hinge: 'left',
    upper: type === 'tall' ? 715 : 0,
    blank: 500,
    bays: DEFAULT_BAYS.map((bay) => ({ ...bay })),
    fronts: true,
    build: 'rigid',
  };
}

// ── Under-stairs geometry ───────────────────────────────────────────────────

/** Height of the stairs' underside at `x` along the run (tall end at 0). */
export function slopeAt(x: number, c: Pick<Cabinet, 'w' | 'h' | 'h2'>): number {
  const t = Math.min(Math.max(x, 0), c.w) / Math.max(1, c.w);
  return c.h - (c.h - c.h2) * t;
}

export interface BayGeometry extends Bay {
  x0: number;
  x1: number;
  /** Heights at the bay's tall and low edges. */
  hi: number;
  lo: number;
}

/** Each bay's span along the run and its heights, clipped to the run length. */
export function bayGeometry(c: Pick<Cabinet, 'w' | 'h' | 'h2' | 'bays'>): BayGeometry[] {
  let x = 0;
  return c.bays.map((bay) => {
    const x0 = x;
    const x1 = Math.min(c.w, x + bay.w);
    x += bay.w;
    return { ...bay, x0, x1, hi: slopeAt(x0, c), lo: slopeAt(x1, c) };
  });
}

// ── Fronts sized from the carcass (B8.2) ────────────────────────────────────

export interface Front {
  kind: 'door' | 'drawer';
  /** Height; for a front cut to a slope, the TALL side. */
  h: number;
  /** The LOW side of a front cut to the stairs' slope. */
  hLow?: number;
  w: number;
  hinge: HingeSide;
  qty: number;
  /** Which part of the cabinet it covers, e.g. "Bay 3, drawer 1". */
  where: string;
}

/**
 * The doors and drawer fronts a cabinet needs, per piece of the cabinet, sized
 * from the carcass. `qty` is per cabinet; multiply by the cabinet's quantity for
 * the order.
 */
export function frontsFor(c: Cabinet): Front[] {
  if (!c.fronts) return [];
  const H = c.h - RULES.frontHeightLess;
  const gap = RULES.frontGap;
  const out: Front[] = [];
  const doorRow = (width: number, height: number, count: number, where: string, singleHinge: HingeSide) => {
    const w = whole((width - RULES.frontWidthLess - gap * (count - 1)) / count);
    if (count === 1) out.push({ kind: 'door', h: height, w, hinge: singleHinge, qty: 1, where });
    else {
      out.push({ kind: 'door', h: height, w, hinge: 'left', qty: 1, where: `${where}, left` });
      out.push({ kind: 'door', h: height, w, hinge: 'right', qty: count - 1, where: `${where}, right` });
    }
  };
  switch (limitsKey(c.type, c.corner)) {
    case 'drawers':
      c.drawerFronts.forEach((h, i) => out.push({ kind: 'drawer', h, w: c.w - RULES.frontWidthLess, hinge: 'none', qty: 1, where: `Drawer ${i + 1}` }));
      break;
    case 'corner-l': {
      // One door on each leg of the L, each the leg's open width less the other leg's depth.
      out.push({ kind: 'door', h: H, w: whole(c.w - c.d - RULES.frontWidthLess), hinge: 'left', qty: 1, where: 'Front leg' });
      out.push({ kind: 'door', h: H, w: whole(c.b - c.d - RULES.frontWidthLess), hinge: 'right', qty: 1, where: 'Return leg' });
      break;
    }
    case 'corner-blind': {
      // The open part beside the blank takes the door, two once one would be too wide.
      const open = c.w - (c.blank ?? 500);
      const count = open - RULES.frontWidthLess > RULES.maxSingleDoor ? 2 : 1;
      doorRow(open, H, count, 'Door beside the blind section', c.blind === 'left' ? 'right' : 'left');
      break;
    }
    case 'corner-diag': {
      const face = Math.round((c.w - c.d) * Math.SQRT2);
      out.push({ kind: 'door', h: H, w: whole(face - RULES.frontWidthLess), hinge: c.hinge, qty: 1, where: 'Angled door' });
      break;
    }
    case 'stairs':
      bayGeometry(c).forEach((bay, i) => {
        if (bay.x1 <= bay.x0 || bay.kind === 'open') return;
        const width = bay.x1 - bay.x0;
        const hi = whole(bay.hi - RULES.frontHeightLess);
        const lo = whole(bay.lo - RULES.frontHeightLess);
        const where = `Bay ${i + 1}`;
        if (bay.kind === 'drawers') {
          const n = Math.max(1, bay.n ?? 1);
          // Equal drawers from the floor up, sized on the bay's LOW side; the top drawer follows the slope.
          const band = whole((lo - gap * (n - 1)) / n);
          for (let k = 0; k < n; k++) {
            const top = k === n - 1;
            out.push({
              kind: 'drawer',
              h: top ? band + (hi - lo) : band,
              ...(top && hi !== lo ? { hLow: band } : {}),
              w: whole(width - RULES.frontWidthLess),
              hinge: 'none',
              qty: 1,
              where: `${where}, drawer ${k + 1} from the floor`,
            });
          }
        } else {
          const count = bay.kind === 'double' ? 2 : 1;
          const w = whole((width - RULES.frontWidthLess - gap * (count - 1)) / count);
          const kind = bay.kind === 'pullout' ? 'drawer' : 'door';
          for (let k = 0; k < count; k++) {
            // Across a double-door bay the slope continues: each door takes its own share.
            const xs = bay.x0 + (width / count) * k;
            const xe = bay.x0 + (width / count) * (k + 1);
            const doorHi = whole(slopeAt(xs, c) - RULES.frontHeightLess);
            const doorLo = whole(slopeAt(xe, c) - RULES.frontHeightLess);
            out.push({
              kind,
              h: doorHi,
              ...(doorHi !== doorLo ? { hLow: doorLo } : {}),
              w,
              hinge: kind === 'drawer' ? 'none' : count === 2 ? (k === 0 ? 'left' : 'right') : 'right',
              qty: 1,
              where: count === 2 ? `${where}, ${k === 0 ? 'left' : 'right'} door` : bay.kind === 'pullout' ? `${where}, pull-out front` : `${where}, door`,
            });
          }
        }
      });
      break;
    case 'tall':
      if ((c.upper ?? 0) > 0) {
        doorRow(c.w, whole(H - c.upper - gap), Math.max(1, c.doors), 'Lower door', c.hinge);
        doorRow(c.w, c.upper, Math.max(1, c.doors), 'Upper door', c.hinge);
      } else {
        doorRow(c.w, H, Math.max(1, c.doors), 'Door', c.hinge);
      }
      break;
    default:
      doorRow(c.w, H, Math.max(1, c.doors), 'Door', c.hinge);
  }
  return out;
}

// ── Checks ──────────────────────────────────────────────────────────────────

export interface CabinetCheck {
  ok: boolean;
  issues: string[];
}

export function checkCabinet(c: Cabinet): CabinetCheck {
  const l = limitsFor(c.type, c.corner);
  const issues: string[] = [];
  const range = (label: string, v: number, [lo, hi]: [number, number]) => {
    if (!(v >= lo && v <= hi)) issues.push(`${label} must be ${lo}–${hi}mm.`);
  };
  range(c.type === 'stairs' ? 'Length' : 'Width', c.w, l.w);
  range(c.type === 'stairs' ? 'Tall-side height' : 'Height', c.h, l.h);
  range('Depth', c.d, l.d);
  if (c.type === 'corner' && c.corner === 'l') range('Return width', c.b, [800, 1200]);
  if (c.type === 'corner' && c.corner === 'blind') range('Blank section', c.blank ?? 500, [300, Math.max(300, c.w - 300)]);
  if (c.type === 'tall' && (c.upper ?? 0) > 0) {
    range('Upper door height', c.upper, [250, Math.max(250, c.h - RULES.frontHeightLess - RULES.frontGap - 400)]);
  }
  if (!(Number.isInteger(c.qty) && c.qty >= 1)) issues.push('Quantity must be at least 1.');

  if (c.type === 'stairs') {
    if (!(c.h2 >= 200 && c.h2 <= c.h - 50)) issues.push(`The low side must be 200mm or more, and at least 50mm below the tall side.`);
    const total = c.bays.reduce((s, b) => s + b.w, 0);
    if (total !== c.w) issues.push(total < c.w ? `The bays leave ${c.w - total}mm of the run unfilled.` : `The bays are ${total - c.w}mm longer than the run.`);
    bayGeometry(c).forEach((bay, i) => {
      if (bay.w < 150) issues.push(`Bay ${i + 1} is narrower than 150mm.`);
      if (bay.kind === 'drawers' && bay.lo / Math.max(1, bay.n ?? 1) < RULES.minDrawerFront) {
        issues.push(`Bay ${i + 1} is too low at its short side for ${bay.n} drawers.`);
      }
    });
  }

  if (c.type === 'drawers' && c.fronts) {
    const want = c.h - RULES.frontHeightLess - RULES.frontGap * (c.drawerFronts.length - 1);
    const sum = c.drawerFronts.reduce((s, v) => s + v, 0);
    if (sum !== want) issues.push(`The drawer fronts add up to ${sum}mm; they must add up to ${want}mm to fill the ${c.h}mm carcass.`);
    c.drawerFronts.forEach((h, i) => {
      if (h < RULES.minDrawerFront) issues.push(`Drawer ${i + 1} is ${h}mm high; the lowest a drawer front can be is ${RULES.minDrawerFront}mm.`);
    });
  }

  const inside = insideHeight(c);
  const sorted = [...c.shelves].sort((a, b) => a - b);
  sorted.forEach((s, i) => {
    if (s < 50 || s > inside - 50) issues.push(`Shelf ${i + 1} at ${s}mm is too close to the top or bottom.`);
    const previous = sorted[i - 1];
    if (previous !== undefined && s - previous < 100) issues.push(`Shelves ${i} and ${i + 1} are only ${s - previous}mm apart.`);
  });
  if (c.shelves.length && c.w - 2 * RULES.board > RULES.maxShelfSpan && c.type !== 'corner') {
    issues.push(`A ${c.w - 2 * RULES.board}mm shelf span will sag; above ${RULES.maxShelfSpan}mm it needs a centre support.`);
  }
  if (c.fronts && (c.type === 'base' || c.type === 'wall' || c.type === 'tall' || c.type === 'wardrobe')) {
    const doorW = (c.w - RULES.frontWidthLess - RULES.frontGap * (c.doors - 1)) / Math.max(1, c.doors);
    if (doorW > RULES.maxSingleDoor) issues.push(`Each door would be ${Math.round(doorW)}mm wide; above ${RULES.maxSingleDoor}mm use two doors.`);
    if (doorW < 120) issues.push(`Each door would be ${Math.round(doorW)}mm wide, too narrow to hinge.`);
  }
  return { ok: issues.length === 0, issues };
}

/** Changes the cabinet's type (and corner kind) and resets sizes and interior to that type's standard. */
export function withType(c: Cabinet, type: CabinetType, corner: CornerKind = c.corner): Cabinet {
  const fresh = newCabinet(type, corner);
  return { ...fresh, qty: c.qty, fronts: c.fronts, build: c.build, blind: c.blind, blank: c.blank ?? fresh.blank };
}

/** After a size change: refit standard shelves, drawer fronts and door count; keep a shopper's own. */
export function refit(c: Cabinet): Cabinet {
  const shelves = c.shelvesCustom ? c.shelves : standardShelves(c, c.shelves.length);
  const drawerFronts = c.drawersCustom ? c.drawerFronts : standardDrawerFronts(c, c.drawerFronts.length);
  return { ...c, shelves, drawerFronts };
}
